import { isValidElement, useEffect, useRef, useState, type HTMLAttributes, type ReactElement, type ReactNode } from 'react'
import { animated, useSpring } from '@react-spring/web'
import TextEngine from 'spring-text-engine'

type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
type HeadingProps = HTMLAttributes<HTMLHeadingElement> & {
  as: HeadingTag
  children: ReactNode
}

type InlineProps = HTMLAttributes<HTMLSpanElement> & {
  children: ReactNode
}

const letterOut = { opacity: 0, y: 24 }
const letterIn = { opacity: 1, y: 0 }
const letterConfig = { tension: 220, friction: 22 }
const intactHeadings = {
  h1: animated.h1,
  h2: animated.h2,
  h3: animated.h3,
  h4: animated.h4,
  h5: animated.h5,
  h6: animated.h6,
}

// TextEngine tokenizes even its word animation into separate text nodes. Browser
// shaping can then lose Persian/Arabic joins, so animate those headings intact.
function hasArabicScript(node: ReactNode): boolean {
  if (typeof node === 'string') return /[\u0600-\u06ff]/u.test(node)
  if (Array.isArray(node)) return node.some(hasArabicScript)
  if (isValidElement(node)) return hasArabicScript((node as ReactElement<{ children?: ReactNode }>).props.children)
  return false
}

/** Respect the operating system's motion preference, including live changes. */
function useReducedMotion() {
  const [reduced, setReduced] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  )

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)')
    const update = () => setReduced(media.matches)
    update()
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [])

  return reduced
}

function useIntactHeadingMotion<T extends HTMLElement>(reducedMotion: boolean) {
  const ref = useRef<T>(null)
  const [visible, setVisible] = useState(reducedMotion)

  useEffect(() => {
    if (reducedMotion) return
    const element = ref.current
    if (!element) return
    if (!('IntersectionObserver' in window)) {
      const timer = setTimeout(() => setVisible(true), 0)
      return () => clearTimeout(timer)
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisible(true)
        observer.disconnect()
      }
    }, { rootMargin: '0px 0px -8% 0px' })
    observer.observe(element)
    return () => observer.disconnect()
  }, [reducedMotion])

  const style = useSpring({
    from: { opacity: 0, transform: 'translate3d(0, 24px, 0)' },
    to: {
      opacity: visible || reducedMotion ? 1 : 0,
      transform: visible || reducedMotion ? 'translate3d(0, 0px, 0)' : 'translate3d(0, 24px, 0)',
    },
    config: letterConfig,
    immediate: reducedMotion,
  })

  return { ref, style }
}

function IntactAnimatedHeading({ as, children, className, reducedMotion, ...rest }: HeadingProps & { reducedMotion: boolean }) {
  const { ref, style } = useIntactHeadingMotion<HTMLHeadingElement>(reducedMotion)
  const IntactHeading = intactHeadings[as]
  return <IntactHeading {...rest} ref={ref} className={className} style={style}>{children}</IntactHeading>
}

function IntactAnimatedHeadingText({ children, className, reducedMotion, ...rest }: InlineProps & { reducedMotion: boolean }) {
  const { ref, style } = useIntactHeadingMotion<HTMLSpanElement>(reducedMotion)
  const classes = [className, 'animated-heading__intact-inline'].filter(Boolean).join(' ')
  return <animated.span {...rest} ref={ref} className={classes} style={style}>{children}</animated.span>
}

/** Keep joined scripts in one DOM text run; stagger Latin letters as before. */
export function AnimatedHeading({ as, children, className, ...rest }: HeadingProps) {
  const reducedMotion = useReducedMotion()
  if (hasArabicScript(children)) {
    return <IntactAnimatedHeading {...rest} as={as} className={className} reducedMotion={reducedMotion}>{children}</IntactAnimatedHeading>
  }

  return (
    <TextEngine
      {...rest}
      tag={as}
      className={className}
      mode="once"
      overflow
      enabled={!reducedMotion}
      letterOut={letterOut}
      letterIn={letterIn}
      letterConfig={letterConfig}
      letterStagger={30}
    >
      {children}
    </TextEngine>
  )
}

/** For linked headings and headings with intentional multi-line layouts. */
export function AnimatedHeadingText({ children, className, ...rest }: InlineProps) {
  const reducedMotion = useReducedMotion()
  if (hasArabicScript(children)) {
    return <IntactAnimatedHeadingText {...rest} className={className} reducedMotion={reducedMotion}>{children}</IntactAnimatedHeadingText>
  }

  return (
    <TextEngine
      {...rest}
      tag="span"
      className={className}
      mode="once"
      overflow
      enabled={!reducedMotion}
      letterOut={letterOut}
      letterIn={letterIn}
      letterConfig={letterConfig}
      letterStagger={30}
    >
      {children}
    </TextEngine>
  )
}
